import { useState, useEffect } from 'react';
import api from '../../api';
import { STAGES } from './crmConstants';

// Create/edit an opportunity. Pass `customerId` to lock the customer (customer detail page),
// otherwise a customer picker is shown (pipeline page).
export default function OpportunityModal({ opportunity, customerId, onClose, onSaved }) {
  const editing = !!opportunity;
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => opportunity ? {
    ...opportunity,
    contact_id: opportunity.contact_id || '',
    owner_id: opportunity.owner_id || '',
    expected_close_date: opportunity.expected_close_date || '',
    lost_reason: opportunity.lost_reason || '',
    notes: opportunity.notes || '',
  } : {
    customer_id: customerId || '',
    contact_id: '',
    owner_id: '',
    title: '',
    stage: 'lead',
    value: 0,
    currency: 'USD',
    probability: '',
    expected_close_date: '',
    lost_reason: '',
    notes: '',
  });

  useEffect(() => {
    api.get('/api/crm/users').then(r => setUsers(r.data));
    if (!customerId && !editing) api.get('/api/customers/').then(r => setCustomers(r.data));
  }, [customerId, editing]);

  useEffect(() => {
    if (!form.customer_id) { setContacts([]); return; }
    api.get(`/api/crm/customers/${form.customer_id}/contacts`).then(r => setContacts(r.data));
  }, [form.customer_id]);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title,
      stage: form.stage,
      value: parseFloat(form.value) || 0,
      currency: form.currency,
      contact_id: form.contact_id ? parseInt(form.contact_id) : null,
      owner_id: form.owner_id ? parseInt(form.owner_id) : null,
      expected_close_date: form.expected_close_date || null,
      lost_reason: form.stage === 'lost' ? (form.lost_reason || null) : null,
      notes: form.notes || null,
    };
    if (form.probability !== '' && form.probability !== null && form.probability !== undefined) {
      payload.probability = parseInt(form.probability);
    }
    try {
      if (editing) {
        // Changing stage resets probability to the stage default unless the user also edited it
        if (form.stage !== opportunity.stage && String(form.probability) === String(opportunity.probability)) {
          delete payload.probability;
        }
        await api.put(`/api/crm/opportunities/${opportunity.id}`, payload);
      } else {
        await api.post('/api/crm/opportunities', { ...payload, customer_id: parseInt(form.customer_id) });
      }
      onSaved();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-screen overflow-y-auto">
        <h3 className="text-lg font-bold mb-4">{editing ? 'Fırsatı Düzenle' : 'Yeni Fırsat'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            {!customerId && !editing && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Müşteri *</label>
                <select required value={form.customer_id} onChange={e => setForm(f => ({ ...f, customer_id: e.target.value, contact_id: '' }))} className="w-full border rounded px-3 py-2">
                  <option value="">Seçiniz</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
              </div>
            )}
            {editing && (
              <div className="col-span-2 text-sm text-gray-500">Müşteri: <span className="font-medium text-gray-800">{opportunity.customer_name}</span></div>
            )}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Başlık *</label>
              <input required value={form.title} onChange={e => set('title', e.target.value)} className="w-full border rounded px-3 py-2" placeholder="ör. Fabrika elektrik panosu yenileme" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Aşama</label>
              <select value={form.stage} onChange={e => set('stage', e.target.value)} className="w-full border rounded px-3 py-2">
                {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Olasılık %</label>
              <input type="number" min="0" max="100" value={form.probability ?? ''} onChange={e => set('probability', e.target.value)} placeholder="Aşamaya göre" className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tahmini Tutar</label>
              <input type="number" step="0.01" value={form.value} onChange={e => set('value', e.target.value)} className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Para Birimi</label>
              <select value={form.currency} onChange={e => set('currency', e.target.value)} className="w-full border rounded px-3 py-2">
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="TRY">TRY</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tahmini Kapanış</label>
              <input type="date" value={form.expected_close_date} onChange={e => set('expected_close_date', e.target.value)} className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sorumlu</label>
              <select value={form.owner_id} onChange={e => set('owner_id', e.target.value)} className="w-full border rounded px-3 py-2">
                <option value="">{editing ? '-' : 'Ben'}</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">İlgili Kişi</label>
              <select value={form.contact_id} onChange={e => set('contact_id', e.target.value)} className="w-full border rounded px-3 py-2" disabled={!form.customer_id}>
                <option value="">-</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.title ? ` (${c.title})` : ''}</option>)}
              </select>
            </div>
            {form.stage === 'lost' && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Kaybedilme Nedeni</label>
                <input value={form.lost_reason} onChange={e => set('lost_reason', e.target.value)} className="w-full border rounded px-3 py-2" placeholder="ör. Fiyat, rakip, proje iptal" />
              </div>
            )}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notlar</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} className="w-full border rounded px-3 py-2" rows={2} />
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button type="submit" disabled={saving} className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">Kaydet</button>
            <button type="button" onClick={onClose} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">İptal</button>
          </div>
        </form>
      </div>
    </div>
  );
}
